"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  FileText,
  Mail,
  Check,
  ArrowRight,
  AlertCircle,
  Building2,
  Stethoscope,
  Phone,
  Clock,
  ChevronDown,
  X,
} from "lucide-react";
import {
  searchHospitals,
  submitClaimDocument,
  requestEmailVerification,
  confirmEmailVerification,
  type HospitalSearchItem,
} from "@/lib/auth-api";
import { Suspense } from "react";

const API = "/api/v1";
type Method = "document" | "domain_email";
type Sheet =
  | null
  | "search"
  | "method"
  | "document_upload"
  | "document_ocr"
  | "document_error"
  | "document_phone"
  | "email_verify"
  | "account"
  | "done";

interface HospitalVerification {
  all_match?: boolean;
  name_match?: boolean;
  addr_match?: boolean;
  hospital_name_db?: string;
  corp_name_doc?: string;
}

interface OCRResult {
  doctor_name?: string;
  birthdate?: string;
  owner_name?: string;
  business_number?: string;
  hospital_verification?: HospitalVerification;
  [key: string]: unknown;
}

interface ErrorDebug {
  _errorSource?: "license" | "business" | "both";
  [key: string]: unknown;
}

// ── 보텀시트 ──
function BottomSheet({
  open,
  onClose,
  title,
  children,
  fullHeight,
}: {
  open: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  fullHeight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        ref={ref}
        className={`animate-slide-up relative w-full max-w-lg rounded-t-3xl bg-white shadow-2xl ${fullHeight ? "h-[92vh]" : "max-h-[85vh]"} flex flex-col`}
      >
        {/* 핸들바 */}
        <div className="flex items-center justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        {/* 헤더 */}
        {title && (
          <div className="flex items-center justify-between border-b border-gray-100 px-5 pb-3">
            <h2 className="text-[17px] font-bold">{title}</h2>
            {onClose && (
              <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            )}
          </div>
        )}
        {/* 컨텐츠 */}
        <div className="flex-1 overflow-y-auto px-5 py-4 pb-[env(safe-area-inset-bottom)]">
          {children}
        </div>
      </div>
      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
}

function ClaimInner() {
  const router = useRouter();
  const [sheet, setSheet] = useState<Sheet>(null);
  const [selected, setSelected] = useState<HospitalSearchItem | null>(null);
  const [method, setMethod] = useState<Method | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // 검색
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<HospitalSearchItem[]>([]);
  const [searching, setSearching] = useState(false);

  // 서류
  const [businessFile, setBusinessFile] = useState<File | null>(null);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [licenseFileError, setLicenseFileError] = useState("");
  const [fileHashError, setFileHashError] = useState("");
  const [ocrResult, setOcrResult] = useState<OCRResult | null>(null);
  const [errorDebug, setErrorDebug] = useState<ErrorDebug | null>(null);
  const [ocrError, setOcrError] = useState("");

  // 이메일
  const [emailPrefix, setEmailPrefix] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState<"input" | "verify">("input");
  const [devCode, setDevCode] = useState("");

  // 계정
  const [accountName, setAccountName] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountPhone, setAccountPhone] = useState("");
  const [loading, setLoading] = useState(false);

  // 로그인
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("");
      })
      .then((d) => {
        setIsLoggedIn(true);
        setIsAdmin(d.user?.user_type === "admin");
      })
      .catch(() => setIsLoggedIn(false));
  }, []);

  const handleNameChange = useCallback((n: string) => setAccountName(n), []);

  // ── 핸들러 ──

  async function handleSearch() {
    if (query.length < 2) return;
    setSearching(true);
    setError("");
    try {
      const results = await searchHospitals(query);
      setSearchResults(results);
      if (results.length === 0) setError("검색 결과가 없습니다");
    } catch {
      setError("검색 실패");
    } finally {
      setSearching(false);
    }
  }

  function selectHospital(h: HospitalSearchItem) {
    setSelected(h);
    setSearchResults([]);
    setSheet("method");
  }

  async function hashFile(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hash = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async function handleOCR() {
    if (!businessFile || !licenseFile) return;
    setUploading(true);
    setError("");
    setLicenseFileError("");
    setFileHashError("");
    setErrorDebug(null);
    setOcrError("");

    const [bh, lh] = await Promise.all([hashFile(businessFile), hashFile(licenseFile)]);
    if (bh === lh) {
      setFileHashError("사업자등록증과 의사면허증이 동일한 파일입니다.");
      setUploading(false);
      return;
    }

    try {
      const fd = new FormData();
      if (selected) fd.append("ykiho", selected.ykiho);
      fd.append("business_doc", businessFile);
      fd.append("license_doc", licenseFile);
      const res = await fetch(`${API}/hospital/claim/ocr-preview`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = data.error || `HTTP ${res.status}`;
        setOcrError(data.message || msg);
        const isLicErr =
          data.ssn_back_exposed ||
          data.wrong_document ||
          msg.includes("면허") ||
          msg.includes("주민등록번호");
        const isBizErr = msg.includes("사업자등록증");
        setErrorDebug({
          _error: msg,
          ...(data.debug || {}),
          _errorSource: isLicErr ? "license" : isBizErr ? "business" : "unknown",
        });
        setSheet("document_error");
        return;
      }
      setOcrResult(data);
      if (data.doctor_name) setAccountName(data.doctor_name);
      setSheet("document_ocr");
    } catch (err) {
      setOcrError(err instanceof Error ? err.message : "서류 분석 오류");
      setSheet("document_error");
    } finally {
      setUploading(false);
    }
  }

  async function handleDocumentSubmit() {
    if (!selected || !ocrResult) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("ykiho", selected.ykiho);
      if (businessFile) fd.append("business_doc", businessFile);
      if (licenseFile) fd.append("license_doc", licenseFile);
      fd.append("phone_name", ocrResult.doctor_name || accountName);
      fd.append("phone_birthdate", ocrResult.birthdate || "");
      fd.append("contact_info", accountPhone);
      if (!isLoggedIn) {
        fd.append("account_email", accountEmail);
        fd.append("account_password", accountPassword);
        fd.append("account_name", accountName);
      }
      const result = await submitClaimDocument(fd);
      setSheet("done");
      setSuccess(
        result.ocr_match_status === "auto_pass"
          ? "자동 검증 통과! 운영팀 검토 후 승인됩니다."
          : "신청이 접수되었습니다. 운영팀이 영업일 기준 24시간 내 연락드립니다.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "신청 실패");
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailRequest() {
    if (!selected || !emailPrefix) return;
    setLoading(true);
    setError("");
    const domain = extractDomain(selected.homepage);
    try {
      const result = await requestEmailVerification(selected.ykiho, `${emailPrefix}@${domain}`);
      setEmailStep("verify");
      if (result.dev_code) setDevCode(result.dev_code);
    } catch (err) {
      setError(err instanceof Error ? err.message : "발송 실패");
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailConfirm() {
    if (!selected || !emailPrefix) return;
    setLoading(true);
    setError("");
    const domain = extractDomain(selected.homepage);
    try {
      await confirmEmailVerification(selected.ykiho, `${emailPrefix}@${domain}`, emailCode);
      setSheet("account");
    } catch (err) {
      setError(err instanceof Error ? err.message : "인증 실패");
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailAccountSubmit() {
    setLoading(true);
    setError("");
    try {
      if (!isLoggedIn && accountEmail && accountPassword) {
        const res = await fetch(`${API}/auth/register`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: accountEmail,
            password: accountPassword,
            name: accountName,
          }),
        });
        if (!res.ok) {
          const d = await res.json();
          throw new Error(d.error || "회원가입 실패");
        }
      }
      setSheet("done");
      setSuccess("이메일 인증 완료! 운영팀 검토 후 병원 관리 권한이 부여됩니다.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "계정 생성 실패");
    } finally {
      setLoading(false);
    }
  }

  // ── 메인 화면 (선택된 병원 + 진행 상태) ──
  return (
    <div className="mx-auto max-w-lg px-5 py-6">
      <header className="mb-6">
        <h1 className="text-[24px] font-extrabold tracking-tight">병원 인증</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          본인의 병원을 인증하고 시술 정보를 직접 관리하세요
        </p>
      </header>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-xs text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 병원 선택 */}
      <button
        onClick={() => setSheet("search")}
        className="w-full rounded-2xl border border-gray-200 bg-white p-4 text-left transition-colors active:bg-gray-50"
      >
        {selected ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-0.5 text-xs font-semibold text-violet-600">선택된 병원</div>
              <div className="text-base font-bold">{selected.name}</div>
              <div className="text-muted-foreground mt-0.5 text-xs">
                {selected.sido} {selected.sggu} · {selected.hospital_type}
              </div>
            </div>
            <ChevronDown className="h-5 w-5 text-gray-400" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-bold text-gray-400">병원을 선택하세요</div>
              <div className="text-muted-foreground mt-0.5 text-xs">병원명 또는 주소로 검색</div>
            </div>
            <Search className="h-5 w-5 text-gray-300" />
          </div>
        )}
      </button>

      {/* 인증 방법 */}
      {selected && !method && (
        <div className="mt-4 space-y-3">
          <p className="text-muted-foreground ml-1 text-xs font-semibold">인증 방법을 선택하세요</p>
          {selected.homepage && (
            <button
              onClick={() => {
                setMethod("domain_email");
                setSheet("email_verify");
              }}
              className="w-full rounded-2xl border-2 border-rose-200 bg-rose-50/30 p-4 text-left transition-colors active:bg-rose-100"
            >
              <div className="flex items-center gap-3">
                <Mail className="h-8 w-8 shrink-0 text-violet-600" strokeWidth={1.5} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">이메일 인증</span>
                    <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[9px] font-bold text-white">
                      간편
                    </span>
                  </div>
                  <div className="text-muted-foreground mt-0.5 text-xs">
                    <span className="font-semibold text-violet-600">
                      @{extractDomain(selected.homepage)}
                    </span>{" "}
                    이메일로 즉시
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300" />
              </div>
            </button>
          )}
          <button
            onClick={() => {
              setMethod("document");
              setSheet("document_upload");
            }}
            className="w-full rounded-2xl border border-gray-200 bg-white p-4 text-left transition-colors active:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 shrink-0 text-violet-600" strokeWidth={1.5} />
              <div className="flex-1">
                <span className="text-sm font-bold">서류 인증</span>
                <div className="text-muted-foreground mt-0.5 text-xs">
                  사업자등록증 + 의사면허증 업로드
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-300" />
            </div>
          </button>
        </div>
      )}

      {/* 진행 상태 표시 — 모든 완료 단계 클릭으로 이전 단계 이동 가능 */}
      {method && sheet !== "done" && (
        <div className="mt-4 space-y-2">
          <StepIndicator
            label="병원 선택"
            done
            onClick={() => {
              setSheet("search");
            }}
          />
          <StepIndicator
            label="인증 방법"
            done
            sub={method === "domain_email" ? "이메일 인증" : "서류 인증"}
            onClick={() => {
              setMethod(null);
              setSheet(null);
            }}
          />
          {method === "document" && (
            <>
              <StepIndicator
                label="서류 업로드"
                done={!!ocrResult}
                active={sheet === "document_upload" || sheet === "document_error"}
                onClick={() => setSheet("document_upload")}
              />
              <StepIndicator
                label="서류 확인"
                done={sheet === "document_phone" || sheet === "account"}
                active={sheet === "document_ocr"}
                onClick={ocrResult ? () => setSheet("document_ocr") : undefined}
              />
              <StepIndicator
                label="본인인증"
                active={sheet === "document_phone"}
                onClick={
                  sheet === "document_phone" || sheet === "account"
                    ? () => setSheet("document_phone")
                    : undefined
                }
              />
            </>
          )}
          {method === "domain_email" && (
            <StepIndicator
              label="이메일 인증"
              done={sheet === "account"}
              active={sheet === "email_verify"}
              onClick={() => setSheet("email_verify")}
            />
          )}
          <StepIndicator
            label="계정 정보"
            active={sheet === "account"}
            onClick={sheet === "account" ? () => setSheet("account") : undefined}
          />
        </div>
      )}

      {/* 완료 */}
      {sheet === "done" && (
        <div className="mt-8 rounded-2xl border border-green-200 bg-green-50 p-8 text-center">
          <Check className="mx-auto mb-3 h-10 w-10 text-green-600" />
          <h2 className="mb-1 text-[17px] font-bold text-green-800">신청 접수 완료</h2>
          <p className="text-sm text-green-700">{success}</p>
          <button
            onClick={() => router.push("/wiki")}
            className="mt-5 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white"
          >
            메인으로
          </button>
        </div>
      )}

      {/* ── 보텀시트들 ── */}

      {/* 병원 검색 */}
      <BottomSheet
        open={sheet === "search"}
        onClose={() => setSheet(null)}
        title="병원 검색"
        fullHeight
      >
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="병원명 또는 주소"
            autoFocus
            className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-rose-400"
          />
          <button
            onClick={handleSearch}
            disabled={searching || query.length < 2}
            className="rounded-xl bg-violet-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            {searching ? "..." : "검색"}
          </button>
        </div>
        {searchResults.map((h) => (
          <button
            key={h.ykiho}
            onClick={() => {
              selectHospital(h);
              setSheet("method");
            }}
            className="block w-full border-b border-gray-100 px-1 py-3 text-left active:bg-rose-50"
          >
            <div className="text-sm font-semibold">{h.name}</div>
            <div className="text-muted-foreground mt-0.5 text-xs">
              {h.sido} {h.sggu} · {h.hospital_type}
              {h.homepage && <span className="ml-1 text-violet-600">· 이메일 인증 가능</span>}
            </div>
          </button>
        ))}
      </BottomSheet>

      {/* 서류 업로드 */}
      <BottomSheet
        open={sheet === "document_upload"}
        onClose={() => setSheet(null)}
        title="서류 업로드"
      >
        <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-700">
          <strong>주민등록번호 뒷자리는 가려서 준비해 주세요</strong>
          <p className="mt-1 text-xs">포스트잇이나 검정 테이프로 가린 후 촬영해주세요.</p>
        </div>
        <div className="space-y-4">
          <FileUpload
            label="사업자등록증"
            icon={Building2}
            file={businessFile}
            onChange={setBusinessFile}
          />
          <FileUpload
            label="의사면허증"
            icon={Stethoscope}
            file={licenseFile}
            onChange={(f) => {
              setLicenseFile(f);
              setLicenseFileError("");
            }}
            error={licenseFileError}
          />
        </div>
        {fileHashError && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
            {fileHashError}
          </div>
        )}
        <button
          onClick={handleOCR}
          disabled={uploading || !businessFile || !licenseFile}
          className="mt-5 w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white disabled:opacity-50"
        >
          {uploading ? "서류 분석 중..." : "서류 분석하기"}
        </button>
        {isAdmin && errorDebug && (
          <div className="mt-4">
            <AdminOCRDebug data={errorDebug} />
          </div>
        )}
      </BottomSheet>

      {/* 서류 분석 결과 */}
      <BottomSheet
        open={sheet === "document_ocr"}
        onClose={() => setSheet(null)}
        title="서류 분석 결과"
      >
        {ocrResult && (
          <OCRResultStep
            ocrResult={ocrResult}
            isAdmin={isAdmin}
            onPhoneVerify={() => setSheet("document_phone")}
            onManualRequest={() => setSheet("account")}
            onReupload={() => {
              setSheet("document_upload");
              setOcrResult(null);
              setBusinessFile(null);
              setLicenseFile(null);
            }}
            onNameChange={handleNameChange}
          />
        )}
      </BottomSheet>

      {/* 서류 분석 오류 */}
      <BottomSheet
        open={sheet === "document_error"}
        onClose={() => setSheet(null)}
        title="서류 분석 오류"
      >
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="mb-1 font-semibold">서류 분석 중 문제가 발생했습니다</p>
              <p>{ocrError}</p>
            </div>
          </div>
        </div>
        {isAdmin && errorDebug && <AdminOCRDebug data={errorDebug} />}
        <div className="mt-4 space-y-3">
          <button
            onClick={() => {
              const src = errorDebug?._errorSource;
              if (src === "license") setLicenseFile(null);
              else if (src === "business") setBusinessFile(null);
              else {
                setBusinessFile(null);
                setLicenseFile(null);
              }
              setSheet("document_upload");
              setOcrError("");
              setErrorDebug(null);
            }}
            className="w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white"
          >
            {errorDebug?._errorSource === "license"
              ? "의사면허증 다시 업로드"
              : errorDebug?._errorSource === "business"
                ? "사업자등록증 다시 업로드"
                : "서류 다시 업로드"}
          </button>
          <button
            onClick={() => setSheet("account")}
            className="w-full rounded-full border-2 border-violet-600 py-3.5 text-sm font-bold text-violet-600"
          >
            <Clock className="mr-1 inline h-4 w-4" />
            수동 검증 요청
          </button>
        </div>
      </BottomSheet>

      {/* 휴대폰 본인인증 */}
      <BottomSheet
        open={sheet === "document_phone"}
        onClose={() => setSheet(null)}
        title="휴대폰 본인인증"
      >
        <div className="mb-4 rounded-xl bg-gray-50 p-4 text-sm">
          <p className="text-muted-foreground mb-2">아래 정보로 본인인증을 진행합니다:</p>
          <p className="font-semibold">이름: {ocrResult?.doctor_name || "-"}</p>
          <p className="font-semibold">생년월일: {ocrResult?.birthdate || "-"}</p>
        </div>
        <button className="w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white">
          <Phone className="mr-1 inline h-4 w-4" />
          휴대폰 본인인증 시작
        </button>
        <button
          onClick={() => setSheet("account")}
          className="mt-3 w-full text-center text-xs font-semibold text-violet-600"
        >
          인증 완료 후 다음 단계로
        </button>
      </BottomSheet>

      {/* 이메일 인증 */}
      <BottomSheet
        open={sheet === "email_verify"}
        onClose={() => setSheet(null)}
        title="이메일 인증"
      >
        {selected && emailStep === "input" ? (
          <>
            <p className="text-muted-foreground mb-4 text-xs">
              병원 공식 도메인 이메일로 인증코드를 받습니다
            </p>
            <div className="flex items-stretch overflow-hidden rounded-xl border border-gray-200 focus-within:border-rose-400">
              <input
                type="text"
                value={emailPrefix}
                onChange={(e) => setEmailPrefix(e.target.value.replace(/[@\s]/g, ""))}
                placeholder="이름"
                autoFocus
                className="min-w-0 flex-1 px-4 py-3 text-sm outline-none"
              />
              <span className="flex items-center border-l border-gray-200 bg-gray-50 px-3 text-sm text-gray-500 select-none">
                @{extractDomain(selected.homepage)}
              </span>
            </div>
            <button
              onClick={handleEmailRequest}
              disabled={loading || !emailPrefix}
              className="mt-4 w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {loading ? "발송 중..." : "인증코드 받기"}
            </button>
          </>
        ) : (
          <>
            <div className="mb-4 rounded-xl bg-rose-50 p-3 text-xs text-violet-600">
              {emailPrefix}@{selected ? extractDomain(selected.homepage) : ""}로 발송됨 (10분 유효)
            </div>
            {devCode && (
              <div className="mb-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-700">
                [개발] 코드: <strong>{devCode}</strong>
              </div>
            )}
            <input
              type="text"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              autoFocus
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-center text-xl tracking-[0.3em] outline-none focus:border-rose-400"
            />
            <button
              onClick={handleEmailConfirm}
              disabled={loading || emailCode.length !== 6}
              className="mt-4 w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {loading ? "확인 중..." : "인증 완료"}
            </button>
            <button
              onClick={() => setEmailStep("input")}
              className="mt-2 w-full text-xs text-violet-600"
            >
              다시 발송
            </button>
          </>
        )}
      </BottomSheet>

      {/* 계정 정보 */}
      <BottomSheet
        open={sheet === "account"}
        onClose={() => setSheet(null)}
        title="관리자 계정 정보"
      >
        <AccountForm
          accountName={accountName}
          setAccountName={setAccountName}
          accountEmail={accountEmail}
          setAccountEmail={setAccountEmail}
          accountPassword={accountPassword}
          setAccountPassword={setAccountPassword}
          accountPhone={accountPhone}
          setAccountPhone={setAccountPhone}
          loading={loading}
          onSubmit={method === "domain_email" ? handleEmailAccountSubmit : handleDocumentSubmit}
        />
      </BottomSheet>
    </div>
  );
}

// ── 공통 컴포넌트 ──

function StepIndicator({
  label,
  done,
  active,
  sub,
  onClick,
}: {
  label: string;
  done?: boolean;
  active?: boolean;
  sub?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left transition-colors ${
        active ? "border border-rose-200 bg-rose-50" : done ? "bg-gray-50" : "opacity-40"
      } ${onClick ? "cursor-pointer active:bg-rose-100" : "cursor-default"}`}
    >
      <div
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
          done
            ? "bg-green-100 text-green-600"
            : active
              ? "bg-rose-100 text-violet-600"
              : "bg-gray-200 text-gray-400"
        }`}
      >
        {done ? <Check className="h-3 w-3" /> : ""}
      </div>
      <span
        className={`text-sm font-semibold ${done ? "text-gray-500" : active ? "text-gray-900" : "text-gray-400"}`}
      >
        {label}
      </span>
      {sub && <span className="text-muted-foreground ml-auto text-xs">{sub}</span>}
    </button>
  );
}

function FileUpload({
  label,
  icon: Icon,
  file,
  onChange,
  error,
}: {
  label: string;
  icon: React.ElementType;
  file: File | null;
  onChange: (f: File | null) => void;
  error?: string;
}) {
  return (
    <div>
      <label className="text-muted-foreground mb-1.5 block text-xs font-semibold">{label} *</label>
      <label
        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed px-4 py-3 ${
          error ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50 active:border-rose-300"
        }`}
      >
        <Icon
          className={`h-5 w-5 shrink-0 ${error ? "text-red-500" : "text-violet-600"}`}
          strokeWidth={1.5}
        />
        <div className="min-w-0 flex-1">
          {file ? (
            <div className={`truncate text-sm font-semibold ${error ? "text-red-700" : ""}`}>
              {file.name}
            </div>
          ) : (
            <div className="text-sm text-gray-400">사진 선택 (JPG, PNG)</div>
          )}
        </div>
        {file && !error && <Check className="h-4 w-4 text-violet-600" />}
        <input
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </label>
      {error && (
        <div className="mt-1.5 text-xs text-red-600">
          <AlertCircle className="mr-0.5 inline h-3 w-3" />
          {error}
        </div>
      )}
    </div>
  );
}

function FormField({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="text-muted-foreground mb-1 block text-xs font-semibold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete || "off"}
        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-rose-400"
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-2.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm font-semibold">{value || "-"}</span>
    </div>
  );
}

// ── OCR 결과 + 수정 ──

function OCRResultStep({
  ocrResult,
  isAdmin,
  onPhoneVerify,
  onManualRequest,
  onReupload,
  onNameChange,
}: {
  ocrResult: OCRResult;
  isAdmin: boolean;
  onPhoneVerify: () => void;
  onManualRequest: () => void;
  onReupload: () => void;
  onNameChange: (n: string) => void;
}) {
  const [editName, setEditName] = useState(ocrResult.doctor_name || "");
  const [editBirth, setEditBirth] = useState(ocrResult.birthdate || "");
  const [editBizName, setEditBizName] = useState(ocrResult.owner_name || "");

  const nameEdited = editName !== (ocrResult.doctor_name || "");
  const birthEdited = editBirth !== (ocrResult.birthdate || "");
  const bizNameEdited = editBizName !== (ocrResult.owner_name || "");
  const anyEdited = nameEdited || birthEdited || bizNameEdited;
  const namesMatch = editBizName && editName && editBizName.trim() === editName.trim();
  const birthValid =
    /^\d{4}-\d{2}-\d{2}$/.test(editBirth) && !editBirth.includes("X") && !editBirth.includes("x");
  const allFilled = editName.trim() !== "" && editBirth.trim() !== "" && editBizName.trim() !== "";
  // 병원 검증 결과
  const hv = ocrResult.hospital_verification;
  const hospitalVerified = hv?.all_match === true;

  const canAutoVerify = !anyEdited && namesMatch && birthValid && hospitalVerified;
  const canManualRequest = allFilled && birthValid;

  useEffect(() => {
    onNameChange(editName);
  }, [editName, onNameChange]);

  return (
    <div>
      <div className="mb-4 space-y-3">
        <EditableField
          label="대표자명 (사업자등록증)"
          value={editBizName}
          onChange={setEditBizName}
          original={ocrResult.owner_name}
          edited={bizNameEdited}
        />
        <EditableField
          label="의사명 (면허증)"
          value={editName}
          onChange={setEditName}
          original={ocrResult.doctor_name}
          edited={nameEdited}
        />
        <EditableField
          label="생년월일 (면허증)"
          value={editBirth}
          onChange={setEditBirth}
          original={ocrResult.birthdate}
          edited={birthEdited}
          placeholder="1992-03-15"
        />
        <InfoRow label="사업자번호" value={ocrResult.business_number} />
      </div>

      {/* 병원 대조 결과 */}
      {hv && (
        <div
          className={`mb-3 rounded-xl border p-3 text-xs ${hv.all_match ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
        >
          <div className="mb-1 font-semibold">
            {hv.all_match ? "병원 정보 일치 확인" : "병원 정보 불일치"}
          </div>
          <div>
            상호: {hv.name_match ? "일치" : "불일치"} ({hv.hospital_name_db} ↔ {hv.corp_name_doc})
          </div>
          <div>주소: {hv.addr_match ? "일치" : "불일치"}</div>
        </div>
      )}

      {!allFilled && <Warn>모든 항목을 입력해주세요.</Warn>}
      {allFilled && !birthValid && (
        <Warn color="red">
          생년월일을 정확히 입력해주세요.{editBirth && ` (현재: ${editBirth})`} 형식: YYYY-MM-DD
        </Warn>
      )}
      {!namesMatch && editBizName && editName && (
        <Warn>대표자명과 의사명이 일치하지 않습니다.</Warn>
      )}
      {anyEdited && (
        <Warn color="blue">자동 인식 결과를 수정하셨습니다. 수동 검증이 필요합니다.</Warn>
      )}

      {isAdmin && <AdminOCRDebug data={ocrResult} />}

      <div className="mt-4 space-y-3">
        {canAutoVerify && (
          <button
            onClick={onPhoneVerify}
            className="w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white"
          >
            휴대폰 본인인증 진행 <ArrowRight className="ml-1 inline h-4 w-4" />
          </button>
        )}
        <button
          onClick={onManualRequest}
          disabled={!canManualRequest}
          className={`w-full rounded-full py-3.5 text-sm font-bold disabled:opacity-40 ${
            canAutoVerify
              ? "border-2 border-violet-600 text-violet-600"
              : "bg-violet-600 text-white"
          }`}
        >
          <Clock className="mr-1 inline h-4 w-4" />
          수동 검증 요청
        </button>
        <button
          onClick={onReupload}
          className="w-full py-2 text-center text-xs font-semibold text-violet-600"
        >
          서류 다시 업로드
        </button>
      </div>
    </div>
  );
}

function EditableField({
  label,
  value,
  onChange,
  original,
  edited,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  original?: string;
  edited: boolean;
  placeholder?: string;
}) {
  return (
    <div
      className={`rounded-xl px-4 py-2.5 ${edited ? "border border-blue-200 bg-blue-50" : "bg-gray-50"}`}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-muted-foreground text-[10px]">{label}</span>
        {edited && <span className="text-[9px] font-semibold text-blue-600">수정됨</span>}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-transparent text-sm font-semibold outline-none placeholder:text-gray-300 ${edited ? "text-blue-700" : ""}`}
      />
      {edited && original && (
        <div className="text-muted-foreground mt-0.5 text-[9px]">인식값: {original}</div>
      )}
    </div>
  );
}

function Warn({
  children,
  color = "amber",
}: {
  children: React.ReactNode;
  color?: "amber" | "red" | "blue";
}) {
  const c = {
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-100 text-blue-700",
  }[color];
  return (
    <div className={`mb-3 rounded-xl border p-3 text-xs ${c}`}>
      <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
      {children}
    </div>
  );
}

interface OCRDebugData {
  usage?: {
    total_cost_krw_formatted?: string;
    total_cost_krw?: number;
    total_cost_usd?: number;
    total_tokens?: number;
    usd_krw_rate?: number;
  };
  nts_verification?: {
    valid?: boolean;
    status?: string;
    message?: string;
  };
  [key: string]: unknown;
}

function AdminOCRDebug({ data }: { data: OCRDebugData | null }) {
  if (!data) return null;
  const usage = data.usage;
  const nts = data.nts_verification;
  return (
    <div className="my-4 space-y-2">
      {usage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700">
          <div className="mb-1 font-bold">AI 비용</div>
          <div>
            비용:{" "}
            <strong>
              {usage.total_cost_krw_formatted ||
                `${Number(usage.total_cost_krw || 0).toFixed(4)}원`}
            </strong>{" "}
            (${Number(usage.total_cost_usd || 0).toFixed(6)}) · 토큰: {usage.total_tokens || 0}
          </div>
          {usage.usd_krw_rate && (
            <div className="text-emerald-500">
              환율: 1 USD = {Number(usage.usd_krw_rate).toFixed(0)} KRW
            </div>
          )}
        </div>
      )}
      {nts && (
        <div
          className={`rounded-xl border p-3 text-xs ${nts.valid ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
        >
          <div className="mb-1 font-bold">국세청 {nts.valid ? "일치" : "불일치"}</div>
          {nts.status && <div>상태: {String(nts.status)}</div>}
          {nts.message && <div>{String(nts.message)}</div>}
        </div>
      )}
      <details className="rounded-xl border border-blue-200 bg-blue-50">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-blue-700">
          [관리자] Raw JSON
        </summary>
        <pre className="overflow-x-auto px-3 pb-3 text-[10px] whitespace-pre-wrap text-blue-600">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function AccountForm({
  accountName,
  setAccountName,
  accountEmail,
  setAccountEmail,
  accountPassword,
  setAccountPassword,
  accountPhone,
  setAccountPhone,
  loading,
  onSubmit,
}: {
  accountName: string;
  setAccountName: (v: string) => void;
  accountEmail: string;
  setAccountEmail: (v: string) => void;
  accountPassword: string;
  setAccountPassword: (v: string) => void;
  accountPhone: string;
  setAccountPhone: (v: string) => void;
  loading: boolean;
  onSubmit: () => void;
}) {
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // 약관 동의
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeContact, setAgreeContact] = useState(false);
  const [agreeAd, setAgreeAd] = useState(false);
  const [agreeThirdParty, setAgreeThirdParty] = useState(false);

  const allRequired = agreeTerms && agreePrivacy && agreeContact;
  const allChecked = allRequired && agreeAd && agreeThirdParty;

  function toggleAll(checked: boolean) {
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreeContact(checked);
    setAgreeAd(checked);
    setAgreeThirdParty(checked);
  }

  const phoneClean = accountPhone.replace(/[^0-9]/g, "");
  const phoneValid = /^01[016789]\d{7,8}$/.test(phoneClean);
  const pwMatch = accountPassword.length >= 8 && accountPassword === passwordConfirm;

  function formatPhone(v: string) {
    const nums = v.replace(/[^0-9]/g, "").slice(0, 11);
    if (nums.length <= 3) return nums;
    if (nums.length <= 7) return nums.slice(0, 3) + "-" + nums.slice(3);
    return nums.slice(0, 3) + "-" + nums.slice(3, 7) + "-" + nums.slice(7);
  }

  const canSubmit =
    accountName.trim() !== "" && accountEmail.trim() !== "" && pwMatch && phoneValid && allRequired;

  return (
    <>
      <p className="text-muted-foreground mb-4 text-xs">
        병원 관리를 위한 계정 정보를 입력해주세요
      </p>
      <div className="space-y-3">
        <FormField
          label="이름 *"
          value={accountName}
          onChange={setAccountName}
          placeholder="홍길동"
        />
        <FormField
          label="이메일 (로그인 ID) *"
          type="email"
          value={accountEmail}
          onChange={setAccountEmail}
          placeholder="admin@hospital.com"
        />
        <FormField
          label="비밀번호 *"
          type="password"
          value={accountPassword}
          onChange={setAccountPassword}
          placeholder="8자 이상"
          autoComplete="new-password"
        />
        {accountPassword.length > 0 && accountPassword.length < 8 && (
          <div className="-mt-1 ml-1 text-xs text-red-500">비밀번호는 8자 이상이어야 합니다</div>
        )}
        <FormField
          label="비밀번호 확인 *"
          type="password"
          value={passwordConfirm}
          onChange={setPasswordConfirm}
          placeholder="비밀번호 재입력"
          autoComplete="new-password"
        />
        {passwordConfirm.length > 0 && accountPassword !== passwordConfirm && (
          <div className="-mt-1 ml-1 text-xs text-red-500">비밀번호가 일치하지 않습니다</div>
        )}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs font-semibold">
            연락처 (휴대폰) *
          </label>
          <input
            type="tel"
            value={accountPhone}
            onChange={(e) => setAccountPhone(formatPhone(e.target.value))}
            placeholder="010-1234-5678"
            className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none ${
              accountPhone && !phoneValid
                ? "border-red-300 focus:border-red-400"
                : "border-gray-200 focus:border-rose-400"
            }`}
          />
          {accountPhone && !phoneValid && (
            <div className="mt-1 ml-1 text-xs text-red-500">올바른 휴대폰 번호를 입력해주세요</div>
          )}
        </div>
      </div>

      {/* 약관 동의 */}
      <div className="mt-5 overflow-hidden rounded-xl border border-gray-200">
        {/* 전체 동의 */}
        <label className="flex cursor-pointer items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 active:bg-gray-100">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(e) => toggleAll(e.target.checked)}
            className="h-5 w-5 rounded accent-violet-600"
          />
          <span className="text-sm font-bold">전체 동의</span>
        </label>

        <div className="space-y-0.5 px-4 py-2.5">
          <AgreeRow
            checked={agreeTerms}
            onChange={setAgreeTerms}
            required
            label="병원관리자 이용약관"
            link="/terms/hospital"
          />
          <AgreeRow
            checked={agreePrivacy}
            onChange={setAgreePrivacy}
            required
            label="개인정보 수집 및 이용"
            link="/privacy"
          />
          <AgreeRow
            checked={agreeContact}
            onChange={setAgreeContact}
            required
            label="인증 및 서비스 연락 수신"
          />
          <AgreeRow checked={agreeAd} onChange={setAgreeAd} label="마케팅 및 프로모션 알림 수신" />
          <AgreeRow
            checked={agreeThirdParty}
            onChange={setAgreeThirdParty}
            label="서비스 개선을 위한 정보 활용"
          />
        </div>
      </div>

      <button
        onClick={onSubmit}
        disabled={loading || !canSubmit}
        className="mt-5 w-full rounded-full bg-violet-600 py-3.5 text-sm font-bold text-white disabled:opacity-40"
      >
        {loading ? "처리 중..." : "신청 완료"}
      </button>
    </>
  );
}

function AgreeRow({
  checked,
  onChange,
  required,
  label,
  link,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  required?: boolean;
  label: string;
  link?: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 rounded accent-violet-600"
      />
      <span className="flex-1 text-xs">
        <span className={required ? "font-semibold text-violet-600" : "font-medium text-gray-400"}>
          {required ? "[필수]" : "[선택]"}
        </span>{" "}
        {label}
      </span>
      {link && (
        <a
          href={link}
          target="_blank"
          className="shrink-0 text-xs text-gray-400 underline"
          onClick={(e) => e.stopPropagation()}
        >
          보기
        </a>
      )}
    </label>
  );
}

function extractDomain(homepage?: string): string {
  if (!homepage) return "";
  try {
    const u = homepage.includes("://") ? new URL(homepage) : new URL("https://" + homepage);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return homepage
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0];
  }
}

export default function HospitalClaimPage() {
  return (
    <Suspense
      fallback={<div className="text-muted-foreground py-20 text-center text-sm">로딩...</div>}
    >
      <ClaimInner />
    </Suspense>
  );
}
